<template>
  <div class="tw-space-y-5">
    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-sm tw-shadow-slate-900/5">
      <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
        <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Reference</div>
      </div>
      <div class="tw-p-5">
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
          <div v-if="isOceanExport" class="lg:tw-col-span-3">
            <InputField id="bkgNumber" label="Booking Number" v-model="orderData.bookingNumber" />
            <div v-if="fieldErrors.bookingNumber" class="tw-mt-1 tw-text-xs tw-text-rose-700">
              {{ fieldErrors.bookingNumber }}
            </div>
          </div>

          <div v-else-if="isOceanImport" class="lg:tw-col-span-4">
            <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
              <label class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">Bill of Lading Number</label>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
                :title="isManualBLMode ? 'Switch to BL search mode' : 'Switch to manual BL entry'"
                @click="toggleBLMode"
              >
                <font-awesome-icon :icon="isManualBLMode ? 'magnifying-glass' : 'pen'" class="tw-h-3 tw-w-3" />
                <span>{{ isManualBLMode ? 'Search' : 'Manual' }}</span>
              </button>
            </div>

            <FilterableDropdown
              v-if="!isManualBLMode"
              placeholder="search for BL number"
              size="sm"
              v-model="orderData.billOfLadingNumber"
              :options="mappedBLs"
              :clearable="true"
              :searchable="true"
              @update:modelValue="selectBL"
              @search-change="onBLSearch"

            />
            <InputField
              v-else
              id="BillOfLadingNumberManual"
              label=""
              v-model="orderData.billOfLadingNumber"
              placeholder="enter new/custom BL number"
            />

            <div class="tw-mt-2 tw-text-xs" v-if="orderData.billOfLadingNumber && String(orderData.billOfLadingNumber).length > 4">
              <span v-if="blStatus?.hasOrder" class="tw-text-amber-700">
                Bill of Lading already has an order and cannot be used to create a new order
              </span>
              <span v-else-if="blStatus?.inCompany && doesBLExist" class="tw-text-emerald-700">
                Already Tracking {{ activeBLs[mappedBLs.indexOf(orderData.billOfLadingNumber)].containers.length }} containers for BL
              </span>
              <span v-else-if="blStatus?.inCompany && !doesBLExist" class="tw-text-slate-700">
                Bill of Lading exists for your company
              </span>
              <span v-else class="tw-text-indigo-700">
                Bill of Lading does not exist, will create a new Bill of Lading {{ orderData.billOfLadingNumber }}
              </span>
            </div>
          </div>

          <div :class="referenceColClass">
            <InputField id="CustomerRef" label="Customer Reference #" v-model="orderData.customerReference" />
          </div>

          <div :class="referenceColClass">
            <InputField id="BillingRef" label="Billing Reference #" v-model="orderData.billingCustomerReference" />
          </div>

          <div v-if="settingsOrderShowLinkedOrderNumberField" class="lg:tw-col-span-3">
            <InputField id="LinkedOrderNumber" label="Linked Master Order #" v-model="orderData.linkedMasterOrderNumber" />
          </div>

          <div v-if="showBrokerFields" class="lg:tw-col-span-3">
            <InputField id="HouseBillOfLading" label="House Bill of Lading Number" v-model="orderData.houseBillOfLading" />
          </div>
        </div>
      </div>
    </div>

    <EditVessel
      v-model:isOpen="showAddVesselModal"
      @close="closeAddVesselModal"
      @saved="onAddVesselSaved"
    />

    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-sm tw-shadow-slate-900/5">
      <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
        <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Locations</div>
      </div>
      <div class="tw-p-5">
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
          <div v-if="showPickUp" class="lg:tw-col-span-4">
            <FilterableDropdown
              :textLabel="pickUpLabel"
              :placeholder="pickUpPlaceholder"
              size="sm"
              v-model="orderData.pickUpLocation"
              :customLabel="(x) => (x.shortName ? x.shortName : x.name)"
              :options="pickUpOptions"
              class="tw-w-full"
              @search-change="onReceivingCustomerSearch"
            />
            <div v-if="fieldErrors.pickUpLocation" class="tw-mt-1 tw-text-xs tw-text-rose-700">
              {{ fieldErrors.pickUpLocation }}
            </div>
            <div v-if="orderData.pickUpLocation?.deliveryAddress" class="tw-mt-2 tw-text-xs tw-text-slate-600">
              <div>{{ orderData.pickUpLocation.deliveryAddress.streetAddress }}</div>
              <div>{{ addressLine3(orderData.pickUpLocation.deliveryAddress) }}</div>
            </div>
          </div>

          <div class="lg:tw-col-span-4">
            <div v-if="!isAddingNewDropoff">
              <FilterableDropdown
                :textLabel="dropOffLabel"
                :placeholder="dropOffPlaceholder"
                size="sm"
                v-model="orderData.dropOffLocation"
                :customLabel="(x) => (x.displayName ? x.displayName : x.name)"
                :options="receivingCustomers || []"
                class="tw-w-full"
                @search-change="onReceivingCustomerSearch"
              />
              <div v-if="fieldErrors.dropOffLocation" class="tw-mt-1 tw-text-xs tw-text-rose-700">
                {{ fieldErrors.dropOffLocation }}
              </div>

              <div class="tw-mt-2" v-if="canCreateReceiver">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                  @click="createReceiver"
                >
                  Create receiver {{ receivingCustomerQuery }}
                </button>
              </div>

              <div v-else-if="orderData.dropOffLocation?.deliveryAddress" class="tw-mt-2 tw-text-xs tw-text-slate-600">
                <div>{{ orderData.dropOffLocation.deliveryAddress.streetAddress }}</div>
                <div>{{ addressLine3(orderData.dropOffLocation.deliveryAddress) }}</div>
              </div>
            </div>

            <div v-else class="tw-space-y-3">
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <div class="sm:tw-col-span-2">
                  <InputField id="streetAddReceiving" label="Street Address" v-model="newDropoff.streetAddress" />
                </div>
                <div>
                  <InputField id="suitNumberReceiving" label="Suite #" v-model="newDropoff.suite" />
                </div>
              </div>
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <InputField id="cityReceiving" label="City" v-model="newDropoff.city" />
                <InputField id="stateReceiving" label="State" v-model="newDropoff.state" />
                <InputField id="zipReceiving" label="Zip" v-model="newDropoff.zipCode" />
              </div>
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <InputField id="contactEmailReceiving" label="Email" v-model="newContact.email" />
                <InputField id="contactNameReceiving" label="Name" v-model="newContact.name" />
                <InputField id="contactPhoneReceiving" label="Phone Number" v-model="newContact.phoneNumber" />
              </div>
              <div class="tw-flex tw-justify-end tw-gap-2">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                  @click="cancelReceiverCreate"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
                  @click="confirmReceiverAddition"
                >
                  Save receiver {{ receivingCustomerQuery }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="!isAddingNewDropoff" class="lg:tw-col-span-4">
            <div v-if="!isAddingNewBillTo">
              <FilterableDropdown
                textLabel="Bill To"
                placeholder="Billing Customer"
                size="sm"
                v-model="orderData.billTo"
                :customLabel="(x) => (x.displayName ? x.displayName : x.name)"
                :options="billingCustomers || []"
                class="tw-w-full"
                @search-change="onBillingCustomerSearch"
              />
              <div v-if="fieldErrors.billTo" class="tw-mt-1 tw-text-xs tw-text-rose-700">
                {{ fieldErrors.billTo }}
              </div>

              <div class="tw-mt-2" v-if="canCreateBillTo">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                  @click="createBillTo"
                >
                  Create customer {{ billingCustomerQuery }}
                </button>
              </div>

              <div v-else-if="orderData.billTo?.billingAddress" class="tw-mt-2 tw-text-xs tw-text-slate-600">
                <div>{{ orderData.billTo.billingAddress.streetAddress }}</div>
                <div>{{ addressLine3(orderData.billTo.billingAddress) }}</div>
              </div>
            </div>

            <div v-else class="tw-space-y-3">
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <div class="sm:tw-col-span-2">
                  <InputField id="streetAddBillTo" label="Street Address" v-model="newBillTo.streetAddress" />
                </div>
                <div>
                  <InputField id="suiteNumberBillTo" label="Suite #" v-model="newBillTo.suite" />
                </div>
              </div>
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <InputField id="cityBillTo" label="City" v-model="newBillTo.city" />
                <InputField id="stateBillTo" label="State" v-model="newBillTo.state" />
                <InputField id="zipBillTo" label="Zip" v-model="newBillTo.zipCode" />
              </div>
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <InputField id="contactEmailBillTo" label="Email" v-model="newContact.email" />
                <InputField id="contactNameBillTo" label="Name" v-model="newContact.name" />
                <InputField id="contactPhoneBillTo" label="Phone Number" v-model="newContact.phoneNumber" />
              </div>
              <div class="tw-flex tw-justify-end tw-gap-2">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                  @click="cancelBillToCreate"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
                  @click="confirmBillToAddition"
                >
                  Save customer {{ billingCustomerQuery }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="settingsOrderShowEmergencyInformation"
      class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-sm tw-shadow-slate-900/5"
    >
      <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
        <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Emergency / Carrier</div>
      </div>
      <div class="tw-p-5">
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
          <div class="lg:tw-col-span-4">
            <InputField id="orderCarrierName" label="Carrier Name" v-model="orderData.carrierName" />
          </div>
          <div class="lg:tw-col-span-4">
            <InputField id="scacCode" label="SCAC" v-model="orderData.scacCode" />
          </div>
          <div class="lg:tw-col-span-4">
            <InputField id="EmergencyPhoneNumber" label="Emergency Response Phone Number" v-model="orderData.emergencyResponsePhoneNumber" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="shouldShowShipLineInfo"
      class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-sm tw-shadow-slate-900/5"
    >
      <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
        <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Sailing / Ship Line</div>
      </div>
      <div class="tw-p-5">
        <div v-if="!showBrokerFields" class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
          <div class="lg:tw-col-span-4">
            <FilterableDropdown
              textLabel="Steamship Line"
              placeholder="Steamship Line"
              size="sm"
              v-model="orderData.shipLineInfo.line"
              :options="steamshipLines"
            />
          </div>
          <div class="lg:tw-col-span-4">
            <div class="tw-flex tw-items-end tw-gap-2">
              <div class="tw-flex-1">
                <FilterableDropdown
                  textLabel="Vessel Name"
                  placeholder="Vessel"
                  size="sm"
                  v-model="orderData.shipLineInfo.vessel"
                  :options="filteredVessels"
                  @search-change="onVesselSearch"
                />
              </div>
              <button
                type="button"
                class="tw-h-10 tw-shrink-0 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                @click="openAddVesselModal"
              >
                Add
              </button>
            </div>
          </div>
          <div class="lg:tw-col-span-4">
            <DatePicker
              :textLabel="isOceanImport ? 'ETA' : 'Vessel Cut Off / LRD'"
              format="MM/DD/YYYY"
              v-model="orderData.shipLineInfo.eta"
            />
          </div>
        </div>

        <div v-else class="tw-space-y-4">
          <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
            <div class="lg:tw-col-span-3"><InputField label="Port of Loading" v-model="orderData.brokerSailingInfo.portOfLoading" id="portOfLoading" /></div>
            <div class="lg:tw-col-span-3"><InputField label="Port of Discharge" v-model="orderData.brokerSailingInfo.portOfDischarge" id="portOfDischarge" /></div>
            <div class="lg:tw-col-span-3"><InputField label="Place of Receipt" v-model="orderData.brokerSailingInfo.placeOfReceipt" id="placeOfReceipt" /></div>
            <div class="lg:tw-col-span-3"><InputField label="Place of Delivery" v-model="orderData.brokerSailingInfo.placeOfDelivery" id="placeOfDelivery" /></div>
          </div>
          <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
            <div class="lg:tw-col-span-4"><InputField label="Vessel / Voyage" v-model="orderData.brokerSailingInfo.vesselVoyage" id="vesselVoyage" /></div>
            <div class="lg:tw-col-span-4"><InputField label="Sailing Date" v-model="orderData.brokerSailingInfo.sailingDate" id="sailingDate" /></div>
            <div class="lg:tw-col-span-4"><InputField label="Arrival Date" v-model="orderData.brokerSailingInfo.arrivalDate" id="arrivalDate" /></div>
          </div>
        </div>
      </div>
    </div>

    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-sm tw-shadow-slate-900/5">
      <div class="tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4">
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div class="tw-text-sm tw-font-semibold tw-text-slate-900">{{ isLandOrder ? 'Loads' : 'Containers' }}</div>
          <div v-if="!isLandOrder" class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-flex tw-items-center tw-gap-2">
              <button
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-w-auto tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
              @click="quickAddContainers"
            >
              Add
            </button>
              <input
                v-model.number="quickAddContainerCount"
                type="number"
                min="1"
                max="50"
                step="1"
                class="tw-w-20 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                aria-label="Number of containers"
                placeholder="Qty"
              />
              <FilterableDropdown
                v-model="quickAddSizeType"
                size="sm"
                placeholder="Size/Type"
                :clearable="false"
                :searchable="true"
                :options="containerSizeTypeOptions"
              />
            </div>
            &nbsp; &nbsp; &nbsp;
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
              @click="addContainer"
            >
              Add Blank Container
            </button>
          </div>
          <button
            v-else
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
            @click="addFreightContents(0)"
          >
            Add Load Item
          </button>
        </div>
      </div>

      <div class="tw-p-5">
        <div v-if="quickAddError" class="tw-mb-3 tw-text-xs tw-text-rose-700">
          {{ quickAddError }}
        </div>
        <div v-if="fieldErrors.containers" class="tw-mb-3 tw-text-xs tw-text-rose-700">
          {{ fieldErrors.containers }}
        </div>
        <template v-if="!isLandOrder">
          <div class="tw-overflow-x-hidden">
            <table class="tw-w-full tw-table-fixed tw-text-left tw-text-sm">
              <thead class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-500">
                <tr>
                  <th class="tw-px-3 tw-py-2">Container #</th>
                  <th class="tw-px-3 tw-py-2">Length</th>
                  <th class="tw-px-3 tw-py-2">Type</th>
                  <th class="tw-px-3 tw-py-2">Seal #</th>
                  <th v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2">Cargo Pieces</th>
                  <th v-if="isRailPickUp" class="tw-px-3 tw-py-2">Pick-Up #</th>
                  <th v-else-if="isRailDropOff" class="tw-px-3 tw-py-2">Release #</th>
                  <th v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2">Description</th>
                  <th v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2">Weight</th>
                  <th class="tw-px-3 tw-py-2"></th>
                </tr>
              </thead>
              <tbody class="tw-divide-y tw-divide-slate-200/70">
                <template v-for="(container, containerIdx) in orderData.containers" :key="containerIdx">
                  <tr>
                    <td class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        v-if="container.isAdded"
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Container"
                        :value="container.containerId && container.containerId.toUpperCase()"
                        @input="container.containerId = String(($event.target as HTMLInputElement).value).toUpperCase()"
                      />
                      <div v-else class="tw-text-slate-800">{{ container.containerId }}</div>
                    </td>
                    <td class="tw-px-3 tw-py-2 tw-min-w-0">
                      <div v-if="container.sizeType" class="tw-text-slate-800">{{ container.sizeType }}</div>
                      <FilterableDropdown
                        v-else
                        placeholder="Size"
                        size="sm"
                        :clearable="false"
                        v-model="container.size"
                        :options="containerSizes"
                      />
                    </td>
                    <td class="tw-px-3 tw-py-2 tw-min-w-0">
                      <div v-if="container.sizeType" class="tw-text-slate-800">{{ container.sizeType }}</div>
                      <FilterableDropdown
                        v-else
                        placeholder="Type"
                        size="sm"
                        :clearable="false"
                        v-model="container.type"
                        :options="containerTypesValues"
                      />
                    </td>
                    <td class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Seal"
                        v-model="container.sealNumber"
                      />
                    </td>
                    <td v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Pieces"
                        v-model="container.freightContents[0].quantity"
                      />
                    </td>
                    <td v-if="isRailPickUp" class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Pick-Up #"
                        v-model="container.pickupNumber"
                      />
                    </td>
                    <td v-else-if="isRailDropOff" class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Release #"
                        v-model="container.releaseNumber"
                      />
                    </td>
                    <td v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Description"
                        v-model="container.freightContents[0].description"
                      />
                    </td>
                    <td v-if="!settingsOrderLtlEnabled" class="tw-px-3 tw-py-2 tw-min-w-0">
                      <input
                        type="text"
                        class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
                        placeholder="Weight"
                        v-model="container.freightContents[0].weight"
                      />
                    </td>
                    <td class="tw-px-3 tw-py-2 tw-text-right">
                      <button
                        type="button"
                        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-h-9 tw-w-9 tw-text-slate-600 hover:tw-bg-slate-50"
                        aria-label="Remove container"
                        @click="removeContainer(containerIdx)"
                      >
                        <font-awesome-icon icon="trash" class="tw-h-4 tw-w-4" />
                      </button>
                    </td>
                  </tr>

                  <tr v-if="settingsOrderLtlEnabled" class="tw-bg-slate-50/40">
                    <td :colspan="ltlColSpan" class="tw-px-3 tw-py-3">
                      <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-overflow-hidden">
                        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200/70 tw-px-4 tw-py-3">
                          <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Freight Contents</div>
                          <button
                            type="button"
                            class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
                            @click="addFreightContents(containerIdx)"
                          >
                            Add Freight Content
                          </button>
                        </div>

                        <div class="tw-overflow-x-auto">
                          <table class="tw-min-w-full tw-text-left tw-text-sm">
                            <thead class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-500">
                              <tr>
                                <th class="tw-px-3 tw-py-2">Qty</th>
                                <th class="tw-px-3 tw-py-2">Packing Type</th>
                                <th class="tw-px-3 tw-py-2">Hazmat</th>
                                <th class="tw-px-3 tw-py-2">Weight</th>
                                <th class="tw-px-3 tw-py-2">Description</th>
                                <th class="tw-px-3 tw-py-2">NMFC #</th>
                                <th class="tw-px-3 tw-py-2">Class</th>
                                <th class="tw-px-3 tw-py-2"></th>
                              </tr>
                            </thead>
                            <tbody class="tw-divide-y tw-divide-slate-200/70">
                              <tr v-for="(fc, fcIdx) in container.freightContents" :key="fcIdx">
                                <td class="tw-px-3 tw-py-2 tw-min-w-[110px]"><InputField id="Quantity" v-model="fc.quantity" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="PackingType" v-model="fc.packingType" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[110px]"><InputField id="IsHazmat" type="checkbox" v-model="fc.isHazmat" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="Weight" v-model="fc.weight" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[240px]"><InputField id="Description" type="textarea" v-model="fc.description" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="NMFCNumber" v-model="fc.nmfcNumber" /></td>
                                <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="nmfcClass" v-model="fc.nmfcClass" /></td>
                                <td class="tw-px-3 tw-py-2 tw-text-right">
                                  <button
                                    type="button"
                                    class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-h-9 tw-w-9 tw-text-slate-600 hover:tw-bg-slate-50"
                                    aria-label="Remove freight content"
                                    @click="removeFreightContents(containerIdx, fcIdx)"
                                  >
                                    <font-awesome-icon icon="trash" class="tw-h-4 tw-w-4" />
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else>
          <div class="tw-overflow-x-auto">
            <table class="tw-min-w-full tw-text-left tw-text-sm">
              <thead class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-500">
                <tr>
                  <th class="tw-px-3 tw-py-2">Quantity</th>
                  <th class="tw-px-3 tw-py-2">Package Type</th>
                  <th class="tw-px-3 tw-py-2">Hazmat</th>
                  <th class="tw-px-3 tw-py-2">Weight</th>
                  <th class="tw-px-3 tw-py-2">Description</th>
                  <th class="tw-px-3 tw-py-2">NMFC #</th>
                  <th class="tw-px-3 tw-py-2">Class</th>
                  <th class="tw-px-3 tw-py-2"></th>
                </tr>
              </thead>
              <tbody class="tw-divide-y tw-divide-slate-200/70">
                <tr v-for="(fc, idx) in landFreightContents" :key="idx">
                  <td class="tw-px-3 tw-py-2 tw-min-w-[110px]"><InputField id="QuantityLand" v-model="fc.quantity" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="PackingTypeLand" v-model="fc.packingType" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[110px]"><InputField id="HazmatLand" type="checkbox" v-model="fc.isHazmat" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="WeightLand" v-model="fc.weight" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[240px]"><InputField id="DescLand" v-model="fc.description" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="NmfcLand" v-model="fc.nmfcNumber" /></td>
                  <td class="tw-px-3 tw-py-2 tw-min-w-[140px]"><InputField id="ClassLand" v-model="fc.nmfcClass" /></td>
                  <td class="tw-px-3 tw-py-2 tw-text-right">
                    <button
                      type="button"
                      class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-h-9 tw-w-9 tw-text-slate-600 hover:tw-bg-slate-50"
                      aria-label="Remove load item"
                      @click="removeFreightContents(0, idx)"
                    >
                      <font-awesome-icon icon="trash" class="tw-h-4 tw-w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

import CreateCustomerMutation from '@/gql/customers/CreateCustomer.gql';
import VesselsQuery from '@/gql/orders/Vessels.gql';

import InputField from '@/components/core/Input/InputField.vue';
import FilterableDropdown from '@/components/core/FilterableDropdown.vue';
import DatePicker from '@/components/core/DateTime/DatePicker.vue';
import EditVessel from '@/components/Vessel/EditVessel.vue';

import { containerSizeTypeMixin } from '@/utils/container';
import { SteamShipLines } from '@/utils/constants';

import { addressMixin } from '@/utils/address';
import { orderTypeMixin } from '@/utils/order';

import { ORDER_TYPE } from '@/types';
import type { NewOrderData } from './orderData';
import { blExistsInActiveBls, findActiveBl } from '@/utils/active_BLs_apollo';

export default defineComponent({
  name: 'NewOrderForm',
  components: {
    InputField,
    FilterableDropdown,
    DatePicker,
    EditVessel,
  },
  mixins: [addressMixin, orderTypeMixin, containerSizeTypeMixin],
  props: {
    orderType: {
      type: String,
      required: true,
    },
    orderData: {
      type: Object as PropType<NewOrderData>,
      required: true,
    },
    billingCustomers: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    receivingCustomers: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    activeBLs: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    blStatus: {
      type: Object as PropType<any | null>,
      default: null,
    },
    terminals: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    settingsOrderShowLinkedOrderNumberField: {
      type: Boolean,
      default: false,
    },
    showVesselInformation: {
      type: Boolean,
      default: false,
    },
    settingsOrderShowEmergencyInformation: {
      type: Boolean,
      default: false,
    },
    settingsOrderLtlEnabled: {
      type: Boolean,
      default: false,
    },
    showBrokerFields: {
      type: Boolean,
      default: false,
    },
    fieldErrors: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
  },
  emits: ['onReceivingCustomerSearch', 'onBillingCustomerSearch', 'onBLSearch'],
  data() {
    return {
      steamshipLines: SteamShipLines,
      isAddingNewDropoff: false,
      isAddingNewBillTo: false,
      isManualBLMode: false,
      quickAddContainerCount: 2,
      quickAddSizeType: '',
      quickAddError: '',
      receivingCustomerQuery: '',
      billingCustomerQuery: '',
      vesselSearchQuery: '',
      vesselSearchDebounceTimer: null,
      showAddVesselModal: false,
      newDropoff: {
        suite: null,
        streetAddress: null,
        city: null,
        state: null,
        zipCode: null,
      },
      newContact: {
        name: null,
        phoneNumber: null,
        email: null,
      },
      newBillTo: {
        suite: null,
        streetAddress: null,
        city: null,
        state: null,
        zipCode: null,
      },
    };
  },
  watch: {
    'orderData.containers': {
      handler() {
        (this as any).normalizeContainers();
      },
      deep: true,
      immediate: true,
    },
    settingsOrderLtlEnabled: {
      handler() {
        (this as any).normalizeContainers();
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    const vm = this as any;
    if (vm.vesselSearchDebounceTimer) {
      clearTimeout(vm.vesselSearchDebounceTimer);
    }
  },
  computed: {
    mappedBLs(): any[] {
      const vm = this as any;
      return (vm.activeBLs || []).map((x: any) => `${x.billOfLadingId}`);
    },
    doesBLExist(): boolean {
      const vm = this as any;
      return blExistsInActiveBls(vm.activeBLs, vm.orderData.billOfLadingNumber);
    },
    showPickUp(): boolean {
      const vm = this as any;
      return !vm.isAddingNewDropoff && !vm.isAddingNewBillTo;
    },
    pickUpLabel(): string {
      const vm = this as any;
      if (vm.isLandOrder) return 'Pick-Up Location';
      if (vm.isOceanExport) return 'Drop-Off Terminal';
      return 'Pick-Up Terminal';
    },
    pickUpPlaceholder(): string {
      const vm = this as any;
      return vm.isLandOrder ? 'Location' : 'Terminal';
    },
    dropOffLabel(): string {
      const vm = this as any;
      if (vm.isLandOrder) return 'Drop-Off Location';
      if (vm.isOceanExport) return 'Pick-Up Customer/Location';
      return 'Drop-Off Customer/Location';
    },
    dropOffPlaceholder(): string {
      const vm = this as any;
      return vm.isLandOrder ? 'Location' : 'Customer Location';
    },
    pickUpOptions(): any[] {
      const vm = this as any;
      if (vm.isLandOrder) return vm.receivingCustomers || [];
      return vm.terminals || [];
    },
    canCreateReceiver(): boolean {
      const vm = this as any;
      return !vm.orderData.dropOffLocation && (vm.receivingCustomers || []).length === 0 && !vm.isAddingNewDropoff && vm.receivingCustomerQuery.length > 0;
    },
    canCreateBillTo(): boolean {
      const vm = this as any;
      return !vm.orderData.billTo && (vm.billingCustomers || []).length === 0 && !vm.isAddingNewBillTo && vm.billingCustomerQuery.length > 0;
    },
    shouldShowShipLineInfo(): boolean {
      const vm = this as any;
      return ((vm.isOceanOrder || (vm.isLandOrder && vm.showBrokerFields)) && vm.showVesselInformation);
    },
    referenceColClass(): string {
      const vm = this as any;
      if (vm.isRailPickUp || vm.isRailDropOff) return 'lg:tw-col-span-6';
      return 'lg:tw-col-span-3';
    },
    filteredVessels(): any[] {
      const vm = this as any;
      // make this a O(N) function not N^2
      // de-dup vessels, skip blank names
      if (!vm.vessels) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const v of vm.vessels) {
        if (v.name && !seen.has(v.name)) {
          seen.add(v.name);
          out.push(v.name);
        }
      }
      return out;
    },
    ltlColSpan(): number {
      const vm = this as any;
      if (vm.settingsOrderLtlEnabled) return 10;
      return 10;
    },
    landFreightContents(): any[] {
      const vm = this as any;
      if (!vm.orderData.containers || vm.orderData.containers.length === 0) return [];
      return vm.orderData.containers[0].freightContents || [];
    },
    containerSizeTypeOptions(): string[] {
      const vm = this as any;
      const sizes = Array.isArray(vm.containerSizes) ? vm.containerSizes : [];
      const types = Array.isArray(vm.containerTypesValues) ? vm.containerTypesValues : [];
      return sizes.flatMap((size: string) => types.map((type: string) => `${size}${type}`));
    },
  },
  methods: {
    ensureReferenceArrays() {
      const vm = this as any;
      if (!Array.isArray(vm.orderData.customerReferences)) {
        vm.orderData.customerReferences = [''];
      }
      if (!Array.isArray(vm.orderData.billingCustomerReferences)) {
        vm.orderData.billingCustomerReferences = [''];
      }
      if (vm.orderData.customerReferences.length === 0) {
        vm.orderData.customerReferences.push('');
      }
      if (vm.orderData.billingCustomerReferences.length === 0) {
        vm.orderData.billingCustomerReferences.push('');
      }
    },
    addCustomerReference() {
      const vm = this as any;
      vm.ensureReferenceArrays();
      vm.orderData.customerReferences.push('');
    },
    removeCustomerReference(idx: number) {
      const vm = this as any;
      vm.ensureReferenceArrays();
      vm.orderData.customerReferences.splice(idx, 1);
      vm.ensureReferenceArrays();
    },
    addBillingReference() {
      const vm = this as any;
      vm.ensureReferenceArrays();
      vm.orderData.billingCustomerReferences.push('');
    },
    removeBillingReference(idx: number) {
      const vm = this as any;
      vm.ensureReferenceArrays();
      vm.orderData.billingCustomerReferences.splice(idx, 1);
      vm.ensureReferenceArrays();
    },
    normalizeContainers() {
      const vm = this as any;
      if (!vm.orderData || !Array.isArray(vm.orderData.containers)) return;
      vm.orderData.containers.forEach((c: any) => {
        if (!Array.isArray(c.freightContents)) {
          c.freightContents = [];
        }
        if (!vm.settingsOrderLtlEnabled && c.freightContents.length === 0) {
          c.freightContents.push({
            quantity: undefined,
            packingType: undefined,
            isHazmat: false,
            weight: undefined,
            description: undefined,
            nmfcNumber: undefined,
            nmfcClass: undefined,
          });
        }
      });
    },
    onBLSearch(val: string) {
      (this as any).$emit('onBLSearch', val);
    },
    toggleBLMode() {
      const vm = this as any;
      vm.isManualBLMode = !vm.isManualBLMode;
    },
    onReceivingCustomerSearch(val: string) {
      (this as any).receivingCustomerQuery = val;
      (this as any).$emit('onReceivingCustomerSearch', val);
    },
    onBillingCustomerSearch(val: string) {
      (this as any).billingCustomerQuery = val;
      (this as any).$emit('onBillingCustomerSearch', val);
    },
    onVesselSearch(val: string) {
      const vm = this as any;
      if (vm.vesselSearchDebounceTimer) {
        clearTimeout(vm.vesselSearchDebounceTimer);
      }
      vm.vesselSearchDebounceTimer = setTimeout(() => {
        vm.vesselSearchQuery = val || '';
      }, 250);
    },
    selectBL(val: any) {
      const vm = this as any;
      const activeBl = findActiveBl(vm.activeBLs, val);
      if (activeBl) {
        const containers = activeBl.containers.slice();
        vm.orderData.containers = containers.map((x: any) => {
          const { id, ...dat } = x;
          const srcFreightContents = Array.isArray(dat.freightContents) ? dat.freightContents : [];
          dat.freightContents = srcFreightContents.map((y: any) => {
            return {
              quantity: y.cargoPieces,
              packingType: y.packingType,
              isHazmat: y.isHazmat,
              weight: y.weight,
              description: y.cargoDescription,
              nmfcNumber: y.nmfcNumber,
              nmfcClass: y.nmfcClass,
            };
          });
          if (!vm.settingsOrderLtlEnabled && dat.freightContents.length === 0) {
            dat.freightContents = [
              {
                quantity: undefined,
                packingType: undefined,
                isHazmat: false,
                weight: undefined,
                description: undefined,
                nmfcNumber: undefined,
                nmfcClass: undefined,
              },
            ];
          }
          return dat;
        });
        if (activeBl.latestVessel) {
          vm.orderData.shipLineInfo.vessel = activeBl.latestVessel.name;
        }
      }
    },

    createReceiver() {
      (this as any).isAddingNewDropoff = true;
    },
    cancelReceiverCreate() {
      (this as any).isAddingNewDropoff = false;
    },
    createBillTo() {
      (this as any).isAddingNewBillTo = true;
    },
    openAddVesselModal() {
      const vm = this as any;
      vm.showAddVesselModal = true;
    },
    closeAddVesselModal() {
      (this as any).showAddVesselModal = false;
    },
    onAddVesselSaved(vessel: any) {
      const vm = this as any;
      vm.orderData.shipLineInfo.vessel = vessel?.name || vm.orderData.shipLineInfo.vessel;
      vm.showAddVesselModal = false;
    },
    cancelBillToCreate() {
      (this as any).isAddingNewBillTo = false;
    },

    confirmReceiverAddition() {
      const vm = this as any;
      vm.$apollo
        .mutate({
          mutation: CreateCustomerMutation,
          variables: {
            module: 'CONTRAK',
            name: vm.receivingCustomerQuery,
            receiverAddress: vm.newDropoff,
            contact: vm.newContact,
          },
        })
        .then((result: any) => {
          vm.newDropoff = {
            suite: null,
            streetAddress: null,
            city: null,
            state: null,
            zipCode: null,
          };
          vm.newContact = {
            name: null,
            phoneNumber: null,
            email: null,
          };
          vm.isAddingNewDropoff = false;
          vm.orderData.dropOffLocation = result.data.createCustomer.customer;
        });
    },

    confirmBillToAddition() {
      const vm = this as any;
      vm.$apollo
        .mutate({
          mutation: CreateCustomerMutation,
          variables: {
            module: 'CONTRAK',
            name: vm.billingCustomerQuery,
            billingAddress: vm.newBillTo,
            contact: vm.newContact,
          },
        })
        .then((result: any) => {
          vm.newBillTo = {
            suite: null,
            streetAddress: null,
            city: null,
            state: null,
            zipCode: null,
          };
          vm.newContact = {
            name: null,
            phoneNumber: null,
            email: null,
          };
          vm.isAddingNewBillTo = false;
          vm.orderData.billTo = result.data.createCustomer.customer;
        });
    },

    removeContainer(containerIdx: number) {
      (this as any).orderData.containers.splice(containerIdx, 1);
    },

    removeFreightContents(containerIdx: number, freightContentIdx: number) {
      (this as any).orderData.containers[containerIdx].freightContents.splice(freightContentIdx, 1);
    },

    addFreightContents(containerIdx: number) {
      const vm = this as any;
      if (!vm.orderData.containers || vm.orderData.containers.length === 0) {
        vm.orderData.containers = [
          {
            containerId: undefined,
            size: undefined,
            type: undefined,
            sealNumber: undefined,
            isAdded: true,
            loadType: 'FullOut',
            freightContents: [],
          },
        ];
      }
      vm.orderData.containers[containerIdx].freightContents.push({
        quantity: undefined,
        packingType: undefined,
        isHazmat: false,
        weight: undefined,
        description: undefined,
        nmfcNumber: undefined,
        nmfcClass: undefined,
      });
    },

    addContainer() {
      (this as any).orderData.containers.push({
        containerId: undefined,
        size: undefined,
        type: undefined,
        sealNumber: undefined,
        isAdded: true,
        freightContents: [
          {
            quantity: undefined,
            packingType: undefined,
            isHazmat: false,
            weight: undefined,
            description: undefined,
            nmfcNumber: undefined,
            nmfcClass: undefined,
          },
        ],
      });
      (this as any).normalizeContainers();
    },

    quickAddContainers() {
      const vm = this as any;
      vm.quickAddError = '';
      const count = Number.parseInt(String(vm.quickAddContainerCount), 10);
      if (!Number.isFinite(count) || count <= 0) {
        vm.quickAddError = 'Quantity must be a whole number greater than 0.';
        return;
      }
      if (count > 50) {
        vm.quickAddError = 'Maximum 50 containers can be added at once.';
        return;
      }
      if (!Number.isInteger(Number(vm.quickAddContainerCount))) {
        vm.quickAddError = 'Quantity must be a whole number.';
        return;
      }
      if (!vm.quickAddSizeType) {
        vm.quickAddError = 'Select container size/type to pre-populate.';
        return;
      }
      if (!vm.containerSizeTypeOptions.includes(vm.quickAddSizeType)) {
        vm.quickAddError = 'Selected size/type is invalid.';
        return;
      }

      const sizeType = String(vm.quickAddSizeType);
      const size = sizeType.slice(0, -2);
      const type = sizeType.slice(-2);

      for (let i = 0; i < count; i += 1) {
        const newContainer: any = {
          containerId: undefined,
          size,
          type,
          sealNumber: undefined,
          isAdded: true,
          pickupNumber: undefined,
          releaseNumber: undefined,
          loadType: 'FullOut',
          freightContents: [],
        };

        vm.orderData.containers.push(newContainer);
      }

      vm.normalizeContainers();
    },
  },
  apollo: {
    vessels: {
      query: VesselsQuery,
      skip() {
        const vm = this as any;
        console.log('skip vessels? ', (!vm.shouldShowShipLineInfo || vm.showBrokerFields));
        return !vm.shouldShowShipLineInfo || vm.showBrokerFields;
      },
    },
  },
});
</script>
